import type { AdminKpisPieChartProps } from "../../models/admin-kpis.models";
import {
  PIE_CHART_CENTER,
  PIE_CHART_RADIUS,
  buildPieAriaLabel,
  buildPiePercentLabels,
  buildPieSlices,
} from "../../utils/admin-kpis.utils";
import "./AdminKpisPie.css";
import { memo, useMemo } from "react";

const AdminKpisPieChart = ({ segments, total, showSegmentPercentLabels = false }: AdminKpisPieChartProps) => {
  const slices = useMemo(() => buildPieSlices(segments, total), [segments, total]);
  const percentLabels = useMemo(
    () => (showSegmentPercentLabels ? buildPiePercentLabels(segments, total) : []),
    [segments, showSegmentPercentLabels, total],
  );
  const ariaLabel = useMemo(() => buildPieAriaLabel(segments, total), [segments, total]);

  return (
    <div className="flex min-w-0 items-center justify-center">
      <svg
        className="admin-kpis-pie-chart"
        viewBox="0 0 100 100"
        role="img"
        aria-label={ariaLabel}
      >
        {total <= 0 && (
          <circle
            className="admin-kpis-pie-chart-empty"
            cx={PIE_CHART_CENTER}
            cy={PIE_CHART_CENTER}
            r={PIE_CHART_RADIUS}
          />
        )}
        {slices.map((slice) => (
          <path
            key={slice.key}
            className={slice.className}
            d={slice.pathData}
          />
        ))}
        <circle
          className="admin-kpis-pie-chart-outline"
          cx={PIE_CHART_CENTER}
          cy={PIE_CHART_CENTER}
          r={PIE_CHART_RADIUS}
        />
        {percentLabels.map((label) => (
          <text
            key={label.key}
            className="admin-kpis-pie-chart-percent-label"
            x={label.x}
            y={label.y}
          >
            {label.text}
          </text>
        ))}
      </svg>
    </div>
  );
};

export default memo(AdminKpisPieChart);
